import { useI18n } from "@core/i18n/i18n";
import { NotificationPreferences, PtNotificationPreferencesStatus } from "@domain/user/user";
import styled from "@emotion/styled";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { isNotNil } from "@utils/assert";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import React, { useCallback } from "react";

interface NotificationPreferencesRowProps {
	label: string;
	preference?: NotificationPreferences;
	onChange: (changedPref: NotificationPreferences) => void;
}

function booleanToPreferenceStatus(isChecked: boolean) {
	return isChecked ? PtNotificationPreferencesStatus.ENABLED : PtNotificationPreferencesStatus.DISABLED;
}

export const NotificationPreferencesRow = ({
	label,
	preference,
	onChange,
}: NotificationPreferencesRowProps): JSX.Element | null => {
	const { format } = useI18n();
	const { isDesktop } = useDimensions();

	const handlePreferenceChange = useCallback(
		(e: CheckboxChangeEvent, key: "email" | "web") => {
			if (!preference) return;
			if (preference[key] !== PtNotificationPreferencesStatus.NA && isNotNil(e.checked)) {
				onChange({
					...preference,
					[key]: booleanToPreferenceStatus(e.checked),
				});
			}
		},
		[preference, onChange],
	);

	const handleMailChange = useCallback(
		(e: CheckboxChangeEvent) => {
			handlePreferenceChange(e, "email");
		},
		[handlePreferenceChange],
	);

	const handleWebChange = useCallback(
		(e: CheckboxChangeEvent) => {
			handlePreferenceChange(e, "web");
		},
		[handlePreferenceChange],
	);

	return isNotNil(preference) ? (
		<Row>
			<div>{label}</div>
			<Checkboxes>
				{preference.email !== PtNotificationPreferencesStatus.NA ? (
					<CheckboxContainer>
						<Checkbox
							inputId="mail"
							checked={preference.email === PtNotificationPreferencesStatus.ENABLED}
							onChange={handleMailChange}
						/>
						{!isDesktop && (
							<div className="p-size-s-bold">{format("settings.notificationPreferences.emailColumn")}</div>
						)}
					</CheckboxContainer>
				) : (
					<EmptyCheckbox />
				)}
				{preference.web !== PtNotificationPreferencesStatus.NA ? (
					<CheckboxContainer>
						<Checkbox
							inputId="web"
							checked={preference.web === PtNotificationPreferencesStatus.ENABLED}
							onChange={handleWebChange}
						/>
						{!isDesktop && <div className="p-size-s-bold">{format("settings.notificationPreferences.webColumn")}</div>}
					</CheckboxContainer>
				) : (
					<EmptyCheckbox />
				)}
			</Checkboxes>
		</Row>
	) : null;
};

const Row = styled.div`
	${row(undefined, "spacebetween", "center")};
	margin-bottom: 8px;

	@media ${physioMeTabletBreakpoint} {
		${stack(8, "center", "flexstart")};
		margin-bottom: 24px;
	}
`;

const Checkboxes = styled.div`
	${row(undefined, "spacearound", "center")};
	width: 180px;

	@media ${physioMeMobileBreakpoint} {
		width: 300px;
	}
`;

const CheckboxContainer = styled.div`
	${row(undefined, "center", "center")};
	width: 90px;

	@media ${physioMeTabletBreakpoint} {
		justify-content: flex-start;
		gap: 8px;
	}
`;

const EmptyCheckbox = styled.div`
	width: 90px;
`;
